/**
 * Setting a password, from the one-time link in the mail.
 *
 * The link lands here as `/set-password?token=…` (password-routes.ts). The
 * token is opaque to this screen: it is posted straight back, and only the
 * server decides whether it is still good.
 *
 * One purpose, one field, one loud button, one clear end. The three ways this
 * can go, and what each one leaves the person holding:
 *
 *   - set          → "you're all set" plus the one way on, into the portal
 *   - too short    → the rule in plain words, the link NOT spent (the server
 *                    checks the rule before it redeems), so trying again works
 *   - link invalid → unknown, expired and already-used are one answer, and it
 *                    is never a dead end: an address, a button, a new link
 *
 * There is no "show password" eye and no strength meter - out of scope by the
 * spec, and the rule is length only.
 *
 * @module web/screens/SetPasswordScreen
 */
import { useState } from "react";
import { PersonEmblem } from "./icons.tsx";

/**
 * What the helper line promises, mirroring `MIN_PASSWORD_LENGTH` in
 * password.ts. The server is the one that enforces it; this only says it out
 * loud, so the two disagreeing costs a slightly stale sentence and nothing more.
 */
export const MIN_PASSWORD_CHARS = 10;

/** The token this visit is carrying, if any. */
export function tokenFromSearch(search: string): string {
  return new URLSearchParams(search).get("token")?.trim() ?? "";
}

type Stage =
  | { kind: "form" }
  | { kind: "saving" }
  | { kind: "done" }
  /** The link is spent. The only way on is a new one. */
  | { kind: "expired" };

export function SetPasswordScreen() {
  const token = tokenFromSearch(window.location.search);
  // No token at all is the same situation as a dead one: nothing to redeem.
  const [stage, setStage] = useState<Stage>(token ? { kind: "form" } : { kind: "expired" });
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (stage.kind === "done") return <Done />;
  if (stage.kind === "expired") return <Expired />;

  return (
    <div className="screen-signin" data-screen="set-password-screen">
      <div className="auth-emblem"><PersonEmblem /></div>
      <h2 className="auth-title">Set your password</h2>
      <p className="auth-sub">Pick a password for your Aditor account. You'll be signed in right after.</p>

      <div className="signin-email-block shown">
        <form className="signin-form" onSubmit={submit}>
          <input
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            placeholder="Your new password"
            aria-label="New password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={stage.kind === "saving"}>
            {stage.kind === "saving" ? "Saving…" : "Set password"}
          </button>
        </form>
        <p className={`signin-hint${error ? " err" : ""}`}>
          {error ?? `At least ${MIN_PASSWORD_CHARS} characters. Nothing else to remember.`}
        </p>
      </div>
    </div>
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || stage.kind === "saving") return;
    setError(null);
    setStage({ kind: "saving" });
    try {
      const res = await fetch("/api/auth/password/set", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setStage({ kind: "done" });
        return;
      }
      // 410 is the one answer for unknown, expired and already used. Nothing
      // the person types here can rescue that link, so the screen changes.
      if (res.status === 410) {
        setStage({ kind: "expired" });
        return;
      }
      setStage({ kind: "form" });
      setError(await failure(res));
    } catch {
      setStage({ kind: "form" });
      setError("Network error. Check your connection and try again.");
    }
    // The typed password is left alone: a short one only needs lengthening.
  }
}

/** The sentence for a refusal that keeps the person on the form. */
async function failure(res: Response): Promise<string> {
  if (res.status === 429) return "Too many attempts. Please wait a few minutes and try again.";
  const code = await res.json().then((b: { error?: string }) => b?.error).catch(() => undefined);
  if (code === "password_too_short") return `Too short - use at least ${MIN_PASSWORD_CHARS} characters.`;
  return "Something went wrong. Please try again or email hey@aditor.ai.";
}

/**
 * The end.
 *
 * A full navigation rather than a state flip: it drops the spent token out of
 * the address bar, and the shell boots against the session cookie the server
 * just set.
 */
function Done() {
  return (
    <div className="screen-signin" data-screen="set-password-screen">
      <div className="auth-emblem"><PersonEmblem /></div>
      <h2 className="auth-title">You're all set.</h2>
      <p className="auth-sub">
        Your password is saved and you're signed in. Next time, use your email and password from any device.
      </p>
      <div className="auth-actions">
        <button type="button" className="btn btn-primary" onClick={() => window.location.assign("/")}>
          Go to your portal
        </button>
      </div>
    </div>
  );
}

/** A spent link, and the one way on from it. */
function Expired() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="screen-signin" data-screen="set-password-screen">
      <div className="auth-emblem"><PersonEmblem /></div>
      <h2 className="auth-title">This link has expired.</h2>
      <p className="auth-sub">Links work once, and not for long. Enter your email and we'll send a fresh one.</p>

      <div className="signin-email-block shown">
        {!sent && (
          <form className="signin-form" onSubmit={request}>
            <input
              type="email"
              autoComplete="email"
              required
              autoFocus
              placeholder="you@example.com"
              aria-label="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <button type="submit" className="btn btn-primary" disabled={sending}>
              {sending ? "Sending…" : "Send a new link"}
            </button>
          </form>
        )}
        <p className={`signin-hint${error ? " err" : ""}`}>
          {sent
            ? <>If an account exists for <strong>{sent}</strong>, a new link is on its way. Check your inbox.</>
            : error ?? "The new link lands in your inbox within a minute."}
        </p>
      </div>
    </div>
  );

  async function request(e: React.FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address || sending) return;
    setError(null);
    setSending(true);
    try {
      const res = await fetch("/api/auth/password/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: address }),
      });
      if (res.status === 429) {
        setError("Too many attempts. Please wait a few minutes and try again.");
        return;
      }
      if (!res.ok) {
        setError("Something went wrong. Please try again or email hey@aditor.ai.");
        return;
      }
      // The server answers 200 whether or not it knows the address, and so does
      // this sentence - it never says which addresses exist.
      setSent(address);
    } catch {
      setError("Network error. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  }
}
